"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AttachmentClient = void 0;
const BaseClient_1 = require("../BaseClient");
class AttachmentClient extends BaseClient_1.BaseClient {
    constructor(baseUrl, tokenProvider) {
        super(baseUrl, tokenProvider);
        this.baseUrl = baseUrl ? baseUrl : BaseClient_1.baseClientDefaults.baseUrl;
    }
    /**
     * Function for fetching the attachment content.
     * WARNING: It must not be used with large files.
     *
     * @param {AttachmentContentRequest} request - request parameters
     */
    getContent(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: '/api/v1/attachment/get',
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                    key: request.key,
                    version: request.version,
                },
                responseType: 'arraybuffer',
            });
            return {
                content: response.data,
                contentType: response.headers['content-type'],
            };
        });
    }
    /**
     * Returns metadata about the specific attachment. Can be used to check virus scan results.
     * Throws error is attachment does not exist.
     *
     * @param request {AttachmentRequest} - request parameters
     *
     * @returns {AttachmentMetadataResponse} - additional metadata about the specific attachment that contains virus scan results
     */
    getMetadata(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: '/api/v1/attachment/metadata',
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                    key: request.key,
                },
            });
            return response.data;
        });
    }
    /**
     * Returns a list of attachments for a specific entity.
     * Returns empty array if no attachments exists.
     *
     * @param request {ListAttachmentRequest} - request parameters
     *
     * @returns {AttachmentResponse[]} - list of attachments
     */
    list(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: '/api/v1/attachment/list',
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                },
            });
            return response.data;
        });
    }
    getVersions(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: '/api/v1/attachment/versions',
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                    key: request.key,
                    // TODO test undefined params
                    limit: request.limit,
                    offset: request.offset,
                },
            });
            return response.data;
        });
    }
    /**
     * Uploads attachment
     * @param request - request params
     * @param progressCallback - reports upload progress. NOTE only environments are supported
     */
    post(request, progressCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance.request({
                url: '/api/v1/attachment/post',
                method: 'POST',
                headers: {
                    'Content-Type': request.contentType,
                    'Content-Length': `${request.contentLength}`,
                },
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                    key: request.key,
                },
                data: request.content,
                onUploadProgress: (event) => {
                    if (progressCallback) {
                        progressCallback(event.loaded);
                    }
                },
            });
            return response.data;
        });
    }
    revertToVersion(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'POST',
                url: '/api/v1/attachment/revertToVersion',
                data: request,
            });
            return response.data;
        });
    }
    delete(request) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.axiosInstance({
                method: 'POST',
                url: '/api/v1/attachment/delete',
                data: request,
            });
        });
    }
    presignDownload(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: '/api/v1/attachment/presign-download',
                params: {
                    entity: request.entity,
                    entityId: request.entityId,
                    key: request.key,
                    downloadFileName: request.downloadFileName,
                    version: request.version,
                },
            });
            return Object.assign(Object.assign({}, response.data), { url: this.baseUrl + response.data.url });
        });
    }
    beginMultipartUpload(request, retryConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance(Object.assign({ method: 'POST', url: '/api/v1/attachment/beginMultipartUpload', params: request }, retryConfig));
            return response.data;
        });
    }
    uploadPart(request, progressCallback, retryConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const { content, contentType } = request, rest = __rest(request, ["content", "contentType"]);
            const response = yield this.axiosInstance(Object.assign({ method: 'POST', url: '/api/v1/attachment/uploadPart', headers: {
                    'Content-Type': contentType,
                }, params: rest, data: content, onUploadProgress: (event) => {
                    if (progressCallback) {
                        progressCallback(event.bytes);
                    }
                } }, retryConfig));
            return response.data;
        });
    }
    completeMultipartUpload(request, retryConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance(Object.assign({ method: 'POST', url: '/api/v1/attachment/completeMultipartUpload', params: request }, retryConfig));
            return response.data;
        });
    }
}
exports.AttachmentClient = AttachmentClient;
